import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { HeaderTemplate } from "Components/templates/Header";
import { FooterTemplate } from "Components/templates/FooterTemplate";
import { useAuth } from "shared/contexts/AuthContext";
import { sendGTMEvent } from "shared/services/GTM";
import { getPlatformType } from "shared/utils/platform";
import { GlobalStyles } from "styles/globalStyles";
import { theme } from "styles/theme";
import { ThemeProvider } from "styled-components";
import {
  Card,
  Content,
  Description,
  Grid,
  HeaderArea,
  Navigation,
  NavigationLink,
  NavigationLogoutButton,
  Page,
  SectionTitle,
  Title,
} from "./styles";
import { PersonalDataForm } from "./PersonalDataForm";
import { NotificationPreferencesForm } from "./NotificationPreferences";
import { AccountLoader } from "./AccountLoader";
import { PrivacySettings } from "./PrivacySettings";

type AccountSection = "personal" | "notifications" | "privacy";

const sectionContent = {
  personal: {
    title: "Dados Pessoais & Acesso",
    description:
      "Consulte seus dados pessoais e as informações usadas para acessar o Voopter.",
  },
  notifications: {
    title: "Preferências de Notificação",
    description:
      "Escolha como deseja receber novidades, alertas e comunicações do Voopter.",
  },
  privacy: {
    title: "Privacidade e Encerramento de Conta",
    description:
      "Consulte as opções relacionadas aos seus dados e ao encerramento da conta.",
  },
};

export default function AccountPage({ section }: { section: AccountSection }) {
  const { status, user, logout } = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);
  const viewedSectionRef = useRef<AccountSection | null>(null);

  useEffect(() => {
    if (
      status !== "authenticated" ||
      !user ||
      viewedSectionRef.current === section
    ) {
      return;
    }

    viewedSectionRef.current = section;
    sendGTMEvent("accountSectionView", {
      platform: getPlatformType(),
      section,
    });
  }, [section, status, user]);

  const handleLogout = async () => {
    if (loggingOut) return;

    setLoggingOut(true);

    try {
      await logout();
      sendGTMEvent("authLogout", {
        platform: getPlatformType(),
        source: "account_navigation",
        result: "success",
      });
      window.location.assign("/");
    } catch {
      sendGTMEvent("authLogout", {
        platform: getPlatformType(),
        source: "account_navigation",
        result: "error",
      });
      setLoggingOut(false);
      window.alert("Não foi possível sair. Tente novamente.");
    }
  };

  if (status === "loading") {
    return (
      <ThemeProvider theme={theme}>
        <GlobalStyles />
        <Page>
          <AccountLoader label="Carregando sua conta..." fullPage />
        </Page>
      </ThemeProvider>
    );
  }

  if (!user) return <Navigate to="/" replace />;

  const content = sectionContent[section];

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <Page>
        <HeaderArea>
          <HeaderTemplate />
        </HeaderArea>
        <Content>
          <Title>Minha Conta</Title>
          <Grid>
            <Navigation aria-label="Seções da minha conta">
              <NavigationLink to="/dados-pessoais">
                Dados Pessoais &amp; Acesso
              </NavigationLink>
              <NavigationLink to="/preferencias-notificacao">
                Preferências de Notificação
              </NavigationLink>
              <NavigationLink to="/privacidade">
                Privacidade e Encerramento
              </NavigationLink>
              <NavigationLogoutButton
                type="button"
                disabled={loggingOut}
                onClick={() => void handleLogout()}
              >
                {loggingOut ? "Saindo..." : "Sair"}
              </NavigationLogoutButton>
            </Navigation>

            <Card>
              <SectionTitle>{content.title}</SectionTitle>
              <Description>{content.description}</Description>

              {section === "personal" && <PersonalDataForm />}

              {section === "notifications" && <NotificationPreferencesForm />}

              {section === "privacy" && <PrivacySettings />}
            </Card>
          </Grid>
        </Content>
        <FooterTemplate />
      </Page>
    </ThemeProvider>
  );
}
